import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@g99-product-web-ui/shared-service';
import {
  ExclamationIcon,
  G99ColoredLogo,
  MfaThumbnail,
  MustMatch,
  PasswordStrengthValidator,
  RegexEnum,
} from '@g99-product-web-ui/shared-util-lib';

import { IForgotPasswordPayload } from '../../interface/IForgot-password.interface';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'g99-forgot-password',
  templateUrl: './forget-password.component.html',
  styleUrl: './forget-password.component.scss',
  standalone: false,
})
export class ForgetPasswordComponent implements OnInit {
  protected currentStage: 'Email' | 'OTP' = 'Email';
  protected errorMessage!: string;
  protected isLoading = false;
  protected forgotPasswordForm!: FormGroup;
  protected isSubmitting = false;
  protected mfaThumbnail = MfaThumbnail;
  protected exclamationSvg = ExclamationIcon;
  protected g99ColoredLogo = G99ColoredLogo;
  protected emailText = 'noreply@growth99.plus';
  protected errorMessages = {
    email: {
      required: 'Email is required',
      pattern: 'Invalid email format',
    },
    password: {
      required: 'New password is required',
      weakPassword:
        'Password must be at least 12 characters long, include an uppercase letter, a number, and a special character.',
    },
    confirmPassword: {
      required: 'Confirm password is required',
      mustMatch: 'Confirm password must match new password',
    },
    confirmationCode: {
      required: 'Confirmation code is required',
    },
  };
  private _destroyRef = inject(DestroyRef);

  constructor(
    private _formBuilder: FormBuilder,
    private _authService: AuthenticationService,
    private _toastMessageService: ToastService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this._initializeForm();

    this.forgotPasswordForm.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (this.errorMessage) {
          this.errorMessage = '';
        }
      });

    const email = this._activatedRoute.snapshot.paramMap.get('email');
    if (email) {
      this.forgotPasswordForm.patchValue({ email: email });
      if (this.forgotPasswordForm.get('email')?.valid) {
        this.requestForConfirmationCode();
      } else {
        this.forgotPasswordForm.get('email')?.markAsTouched();
      }
    }
  }

  /**
   * Checks if a form control is invalid.
   * Used for showing error messages for specific fields in the form.
   *
   * @param controlName - Name of the form control to validate.
   * @returns `true` if the control is invalid and has been touched; otherwise, `false`.
   */
  protected isInvalid(controlName: string): boolean {
    const control = this.f[controlName];
    return !!control?.touched && !!control?.invalid;
  }

  /**
   * Checks if a form control is invalid.
   * Used for showing error messages for specific fields in the form.
   *
   * @param controlName - Name of the form control to validate.
   * @returns `true` if the control is invalid and has been touched; otherwise, `false`.
   */
  protected requestForConfirmationCode(): void {
    this.errorMessage = '';
    const userName = this.f['email'].value;
    this.isLoading = true;
    this._authService.getConfirmationCodeForPasswordReset(userName).subscribe({
      next: () => {
        this.isLoading = false;
        this._toastMessageService.showSuccess(
          'We have sent a verification code to your email to help you change your password.'
        );
        this.currentStage = 'OTP';
        this.forgotPasswordForm.get('email')?.disable();
        this._applyDynamicValidation();
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage =
          error.error.errorMessage || 'Unable to send verification code';
        this._toastMessageService.showError(this.errorMessage);
      },
    });
  }

  /**
   * Requests to reset the user's password by submitting the username, password, and confirmation code.
   * This method triggers the API to reset the password based on the provided details.
   *
   * @returns An Observable that handles the response from the API, showing a success or error message.
   */
  protected processPasswordReset(): void {
    if (this.forgotPasswordForm.invalid) {
      return;
    }
    this.errorMessage = '';
    this.isLoading = true;
    const payload: IForgotPasswordPayload = {
      username: this.f['email'].value,
      password: this.f['password'].value,
      confirmPassword: this.f['confirmPassword'].value,
      confirmationCode: this.f['confirmationCode'].value,
    };
    this._authService.forgotPassword(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this._toastMessageService.showSuccess(
          'Your password has been successfully reset.'
        );
        this._router.navigate(['/']);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage =
          error.error.errorMessage ||
          'The confirmation code you entered is invalid. Please check the code and try again.';
        this._toastMessageService.showError(this.errorMessage);
      },
    });
  }
  /**
   * Initializes the form with validation and custom password matching validator.
   */
  private _initializeForm(): void {
    // Initialize the form with only the email field and no validation on OTP, password, or confirmPassword initially
    this.forgotPasswordForm = this._formBuilder.group({
      email: [''],
      password: [''],
      confirmPassword: [''],
      confirmationCode: [''],
    });

    // Dynamically apply validation based on the current stage
    this._applyDynamicValidation();
  }

  /**
   * Dynamically applies validation rules to the form controls based on the current stage of the forgot password process.
   * This method ensures that the correct fields are validated according to the user's current stage in the workflow.
   */
  private _applyDynamicValidation(): void {
    // Check the current stage and apply corresponding validation dynamically
    switch (this.currentStage) {
      case 'Email':
        this._setEmailValidation();
        break;
      case 'OTP':
        this._setOtpStageValidation();
        break;
      default:
        break;
    }
  }

  /**
   * Sets validation rules for the email field during the "Email" stage.
   * Ensures the email field is required and matches a valid email format.
   */
  private _setEmailValidation(): void {
    // In email stage, validate only the email field
    this.forgotPasswordForm
      .get('email')
      ?.setValidators([
        Validators.required,
        Validators.pattern(RegexEnum.email),
      ]);
    // No need to clear validators for OTP, password, and confirmPassword fields since they don't have validation yet
    this.forgotPasswordForm.updateValueAndValidity();
  }

  /**
   * Sets validation rules for the fields during the "OTP" stage.
   * Ensures validation for the confirmation code, password, and confirm password fields,
   * and applies a custom validator to check if the password and confirmPassword match.
   */
  private _setOtpStageValidation(): void {
    // Apply full validation for OTP stage
    this.forgotPasswordForm
      .get('confirmationCode')
      ?.setValidators([Validators.required]);
    this.forgotPasswordForm
      .get('password')
      ?.setValidators([Validators.required, PasswordStrengthValidator()]);

    // Confirm password validation (should match the password)
    this.forgotPasswordForm
      .get('confirmPassword')
      ?.setValidators([Validators.required]);

    // Set custom validator to check if password and confirmPassword match
    this.forgotPasswordForm.setValidators(
      MustMatch('password', 'confirmPassword')
    );

    // Update form validity after adding all validators
    this.forgotPasswordForm.updateValueAndValidity();
  }

  /**
   * Provides a shorthand reference to the form controls in the forgot password form.
   *
   * @returns An object containing all form controls for easy access.
   */
  protected get f() {
    return this.forgotPasswordForm.controls;
  }
}
